// src/pages/plans/ArtAdvisorDemo.jsx
// Landing du plan Art Advisor (/plans/art-advisor/demo). Refaite le 2026-10-05 sur le modèle
// des pages /notaires et /cercle-experts : une promesse, des engagements nets, des étapes
// numérotées, un prix clair, des captures réelles.
//
// Chaque phrase correspond à du code livré (vérifié) :
//  • dossiers clients sans compte, notes privées, 0 % de commission sur les missions propres
//    (trigger advisor_mission_coherence), présentation plein écran, propositions d'acquisition ;
//  • mises en relation incluses pour les abonnés (trigger expert_clients_introduction_guard).
// « Jusqu'à 5 clients et 1 assistant » : décision du 2026-10-06 ; la limite de clients est imposée
// en base (trigger enforce_advisor_clients_limit), celle d'assistants seulement côté interface.
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDemoPage } from "@/lib/useDemoPage";
import { BRAND_NAME } from "@/config/brand";
import BrandMark from "@/components/BrandMark";

const COMMITMENTS = [
  { t: "Aucune commission sur vos missions", d: `Sur vos propres clients, ${BRAND_NAME} ne prélève rien : la base de données l'interdit.` },
  { t: "Des notes qui restent à vous", d: `Vos notes internes ne sont visibles ni de votre client ni de l'équipe ${BRAND_NAME}.` },
  { t: "Chacun voit ce que vous autorisez", d: "Vous invitez vos clients avec des accès limités, que vous retirez à tout moment." },
  { t: "Les collectionneurs qui vous contactent, inclus", d: "Les mises en relation avec les collectionneurs de la plateforme sont comprises dans votre abonnement, sans limite." },
];

const STEPS = [
  { t: "Créez le dossier de votre client", d: "Même sans compte : ses collections, missions et devis restent rattachés au dossier. Vous l'invitez quand vous voulez." },
  { t: "Documentez ses œuvres", d: "Fiches, pièces justificatives et niveau de complétude, œuvre par œuvre." },
  { t: "Présentez et proposez", d: "Mode présentation plein écran en rendez-vous ; propositions d'acquisition à comparer, avec les réactions du client." },
  { t: "Facturez vos missions", d: "Devis, suivi des missions et de la facturation, depuis le même espace." },
];

const COSTS = [
  { k: "Abonnement", v: "99 €", sub: "par mois", d: "Jusqu'à 5 collections clients, tous les outils du plan." },
  { k: "Mises en relation", v: "Incluses", sub: "illimitées", d: "Les collectionneurs de la plateforme qui vous contactent ne vous coûtent rien de plus." },
  { k: "Commission", v: "0 %", sub: "sur vos missions", d: "Vos honoraires et vos clients vous appartiennent, jamais de prélèvement." },
];

// Les fichiers sont déposés dans public/images/advisor/ ; une capture absente n'est pas affichée.
const SHOT_CANDIDATES = [
  { src: "/images/advisor/dashboard-multi-clients.png", title: "Vos dossiers clients réunis", description: "Chaque client a son dossier, avec ou sans compte : vous l'invitez quand vous voulez." },
  { src: "/images/advisor/presentation-plein-ecran.png", title: "Le mode présentation", description: "Présentez une collection œuvre par œuvre, en plein écran, lors d'un rendez-vous." },
  { src: "/images/advisor/propositions-detail.png", title: "Les propositions d'acquisition", description: "Une sélection d'œuvres, un prix demandé et un argumentaire par œuvre, partagés par un lien de 14 jours." },
];

export default function ArtAdvisorDemo() {
  const navigate = useNavigate();
  const trackCta = useDemoPage("art-advisor");
  const [shots, setShots] = useState([]);

  useEffect(() => {
    let alive = true;
    SHOT_CANDIDATES.forEach((s) => {
      const img = new Image();
      img.onload = () => {
        if (alive) setShots((prev) => (prev.some((p) => p.src === s.src) ? prev : [...prev, s].sort((a, b) => SHOT_CANDIDATES.findIndex((c) => c.src === a.src) - SHOT_CANDIDATES.findIndex((c) => c.src === b.src))));
      };
      img.src = s.src;
    });
    return () => { alive = false; };
  }, []);

  const go = (path) => { trackCta(path); navigate(path); };
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div className="min-h-screen bg-[#f9f7f4] text-[#1a1a1a]" style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}>
      {/* NAV */}
      <header className="sticky top-0 z-30 bg-[#f9f7f4]/95 backdrop-blur border-b border-[#e5e0d8]">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <BrandMark className="text-2xl tracking-[0.15em]" />
          <div className="flex items-center gap-4">
            <button onClick={() => scrollTo("fonctions")} className="hidden md:inline text-sm text-[#6b6459] hover:text-black">Comment ça marche</button>
            <button onClick={() => scrollTo("tarif")} className="hidden md:inline text-sm text-[#6b6459] hover:text-black">Tarif</button>
            <Link to="/plans/art-advisor-premium/demo" className="hidden md:inline text-sm text-[#6b6459] hover:text-black">Premium</Link>
            <button onClick={() => go("/register?plan=art_advisor")} className="text-sm px-4 py-2 bg-[#1a1a1a] text-[#f9f7f4] rounded-md hover:bg-black">
              Démarrer maintenant
            </button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="max-w-6xl mx-auto px-6 pt-14 md:pt-20 pb-14">
        <div className="text-xs tracking-[0.18em] uppercase text-[#8a8378] mb-5">Plan Art Advisor · pour les conseils en art</div>
        <h1 className="text-4xl md:text-5xl font-bold leading-[1.1] max-w-4xl">
          Tous vos clients, dans un seul espace confidentiel.
        </h1>
        <p className="mt-6 text-lg text-[#4a453d] leading-relaxed max-w-2xl">
          Dossiers clients, missions, devis et rapports au même endroit. Vous gardez la relation, la décision et vos honoraires.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <button onClick={() => go("/register?plan=art_advisor")} className="px-6 py-3 bg-[#1a1a1a] text-[#f9f7f4] rounded-md text-base hover:bg-black">
            Démarrer maintenant
          </button>
          <button onClick={() => go("/contact?plan=art_advisor&demo=true")} className="px-6 py-3 border border-[#1a1a1a] rounded-md text-base hover:bg-[#1a1a1a] hover:text-[#f9f7f4] transition-colors">
            Tester avec un dossier
          </button>
        </div>
        <p className="mt-4 text-sm text-[#8a8378]">99 € par mois · aucune commission sur vos missions · mises en relation incluses</p>
      </section>

      {/* ENGAGEMENTS */}
      <section className="border-y border-[#e5e0d8] bg-white">
        <div className="max-w-6xl mx-auto px-6 py-14">
          <div className="text-xs tracking-[0.18em] uppercase text-[#8a8378] mb-3">Nos engagements</div>
          <h2 className="text-3xl font-bold mb-8 max-w-3xl">Vos clients restent les vôtres.</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {COMMITMENTS.map((c) => (
              <div key={c.t} className="border border-[#e5e0d8] rounded-xl p-5 bg-[#f9f7f4]">
                <h3 className="font-bold mb-2">{c.t}</h3>
                <p className="text-sm text-[#4a453d] leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMMENT ÇA MARCHE */}
      <section id="fonctions" className="max-w-6xl mx-auto px-6 py-16">
        <div className="text-xs tracking-[0.18em] uppercase text-[#8a8378] mb-3">Comment ça marche</div>
        <h2 className="text-3xl font-bold mb-10">Du premier rendez-vous à la facture, en quatre temps.</h2>
        <ol className="grid md:grid-cols-4 gap-6">
          {STEPS.map((s, i) => (
            <li key={s.t}>
              <div className="w-9 h-9 rounded-full bg-[#1a1a1a] text-[#f9f7f4] flex items-center justify-center text-sm font-bold mb-4">{i + 1}</div>
              <h3 className="font-bold mb-1">{s.t}</h3>
              <p className="text-sm text-[#4a453d] leading-relaxed">{s.d}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* CAPTURES RÉELLES */}
      {shots.length > 0 && (
        <section className="border-y border-[#e5e0d8] bg-white">
          <div className="max-w-6xl mx-auto px-6 py-16">
            <div className="text-xs tracking-[0.18em] uppercase text-[#8a8378] mb-3">Dans l'espace</div>
            <h2 className="text-3xl font-bold mb-10">Ce que vous verrez, tel quel.</h2>
            <div className={`grid gap-8 ${shots.length > 2 ? "md:grid-cols-3" : shots.length > 1 ? "md:grid-cols-2" : ""}`}>
              {shots.map((shot) => (
                <figure key={shot.src} className="bg-[#f9f7f4] rounded-2xl p-4 border border-[#e5e0d8]">
                  <img src={shot.src} alt={shot.title} className="w-full rounded-lg border border-[#ece6da]" loading="lazy" />
                  <figcaption className="mt-4 px-1">
                    <span className="block text-lg font-bold mb-1">{shot.title}</span>
                    <span className="block text-sm text-[#4a453d] leading-relaxed">{shot.description}</span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* TARIF */}
      <section id="tarif" className="max-w-6xl mx-auto px-6 py-16">
        <div className="text-xs tracking-[0.18em] uppercase text-[#8a8378] mb-3">Ce que cela coûte</div>
        <h2 className="text-3xl font-bold mb-2">Un abonnement, rien d'autre.</h2>
        <p className="text-[#4a453d] mb-10 max-w-2xl">Pas de commission cachée : le prix affiché est le seul prix.</p>
        <div className="grid md:grid-cols-3 gap-5">
          {COSTS.map((c, i) => (
            <div key={c.k} className={`rounded-2xl p-6 border ${i === 0 ? "border-[#1a1a1a] bg-white" : "border-[#e5e0d8] bg-white"}`}>
              <div className="text-sm text-[#6b6459] mb-3">{c.k}</div>
              <div className="text-3xl font-bold">{c.v}</div>
              <div className="text-xs text-[#8a8378] mt-1">{c.sub}</div>
              <p className="mt-4 text-sm text-[#4a453d] leading-relaxed">{c.d}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 rounded-2xl border border-[#d7ccb5] bg-white p-6 flex flex-wrap items-center justify-between gap-4">
          <p className="text-[#4a453d] max-w-2xl">
            <strong>Plus de 5 clients, des rapports à votre nom, les analyses Vision pour chacun ?</strong> Le plan Premium (199 € par mois) lève la limite de clients.
          </p>
          <Link to="/plans/art-advisor-premium/demo" onClick={() => trackCta("/plans/art-advisor-premium/demo")} className="px-5 py-2.5 border border-[#1a1a1a] rounded-md text-sm hover:bg-[#1a1a1a] hover:text-[#f9f7f4] transition-colors">
            Découvrir Art Advisor Premium
          </Link>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="bg-[#1a1a1a] text-[#f9f7f4]">
        <div className="max-w-4xl mx-auto px-6 py-16 text-center">
          <h2 className="text-3xl font-bold mb-4">Réunissez vos clients dans un espace qui vous ressemble.</h2>
          <p className="text-[#d8d2c6] leading-relaxed max-w-2xl mx-auto mb-8">
            Commencez avec un premier dossier : vous verrez en quelques minutes si cela convient à votre façon de travailler.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <button onClick={() => go("/register?plan=art_advisor")} className="px-7 py-3 bg-[#c9a961] text-[#1a1a1a] font-semibold rounded-md hover:bg-[#b9975a]">
              Démarrer maintenant
            </button>
            <button onClick={() => go("/contact?plan=art_advisor")} className="px-7 py-3 border border-[#f9f7f4]/60 rounded-md hover:bg-[#f9f7f4]/10">
              Parler à l'équipe
            </button>
          </div>
        </div>
      </section>

      <footer className="max-w-6xl mx-auto px-6 py-10 text-xs text-[#8a8378] flex flex-wrap gap-4 justify-between">
        <span>© {new Date().getFullYear()} {BRAND_NAME}</span>
        <span className="flex gap-4">
          <Link to="/mentions-legales" className="hover:text-black">Mentions légales</Link>
          <Link to="/privacy-policy" className="hover:text-black">Confidentialité</Link>
        </span>
      </footer>
    </div>
  );
}
